import { ConfigurationType, ExistingSystemType } from 'data/types';
import { getDuctedPairZone } from 'utils/ducted-design';
import {
  DuctedNewSystemSlotClaims,
  DuctedNewSystemSlotsPlan,
  DuctedPairedCondenser,
  PlanDuctedNewSystemSlotsParams
} from 'utils/ducted-new-systems-types';

export function isDuctedNewSystemSlot(system: ExistingSystemType): boolean {
  return system.type === 'ducted' && system.components.length === 0;
}

export function getDuctedNewSystemSlots(
  existingSystems: ExistingSystemType[]
): ExistingSystemType[] {
  return existingSystems.filter(isDuctedNewSystemSlot);
}

export function getDuctedNewSystemAnchorSlot(
  existingSystems: ExistingSystemType[]
): ExistingSystemType | null {
  return getDuctedNewSystemSlots(existingSystems)[0] ?? null;
}

function getPairedCondensers(
  design: ConfigurationType
): DuctedPairedCondenser[] {
  return design.condensers.flatMap((condenser) => {
    const zone = getDuctedPairZone(condenser);
    if (!zone) return [];
    return [
      {
        condenserId: condenser.uniqueId,
        linkedSystemId: zone.existingSystemId ?? null
      }
    ];
  });
}

function claimLinkedSlots(
  condensers: DuctedPairedCondenser[],
  slotIds: string[]
): DuctedNewSystemSlotClaims {
  const keptCondenserIds = new Set<string>();
  const claimedSlotIds = new Set<string>();
  condensers.forEach(({ condenserId, linkedSystemId }) => {
    if (
      linkedSystemId !== null &&
      slotIds.includes(linkedSystemId) &&
      !claimedSlotIds.has(linkedSystemId)
    ) {
      claimedSlotIds.add(linkedSystemId);
      keptCondenserIds.add(condenserId);
    }
  });
  return {
    keptCondenserIds,
    freeSlotIds: slotIds.filter((slotId) => !claimedSlotIds.has(slotId))
  };
}

export function planDuctedNewSystemSlots({
  design,
  existingSystems,
  newSlotId
}: PlanDuctedNewSystemSlotsParams): DuctedNewSystemSlotsPlan {
  const slotIds = getDuctedNewSystemSlots(existingSystems).map(
    (system) => system.uniqueId
  );
  const condensers = getPairedCondensers(design);
  const { keptCondenserIds, freeSlotIds } = claimLinkedSlots(
    condensers,
    slotIds
  );
  const unlinkedCondenserIds = condensers
    .filter(({ condenserId }) => !keptCondenserIds.has(condenserId))
    .map(({ condenserId }) => condenserId);

  const reusedSlotIds = freeSlotIds.slice(0, unlinkedCondenserIds.length);
  const spareSlotIds = freeSlotIds.slice(unlinkedCondenserIds.length);
  const addIds = unlinkedCondenserIds
    .slice(reusedSlotIds.length)
    .map(() => newSlotId());
  const linkTargetIds = [...reusedSlotIds, ...addIds];
  const anchorSlotId = getDuctedNewSystemAnchorSlot(existingSystems)?.uniqueId;

  return {
    addIds,
    links: unlinkedCondenserIds.map((condenserId, index) => ({
      condenserId,
      existingSystemId: linkTargetIds[index]
    })),
    removeIds: spareSlotIds.filter((slotId) => slotId !== anchorSlotId)
  };
}
